import type { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { BankIDAuth } from "@krea/shared/features/bankID";
import { CountryCode } from "@krea/shared/types/common";

import { useLoginStore } from "./loginStore";

interface BankIDLoginProps {
  onSwitchToPassword: () => void;
}

export const BankIDLogin: FC<BankIDLoginProps> = ({ onSwitchToPassword }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCountry, setSelectedCountry] = useLoginStore((state) => [
    state.countryCode,
    state.setCountryCode,
  ]);

  const handleCountryChange = (value: CountryCode) => {
    setSelectedCountry(value);
    localStorage.setItem("selectedCountry", value);
  };
  const from = location.state?.from?.pathname || "/";

  const handleSuccess = () => {
    navigate(from, { replace: true });
  };

  const handleError = (error: { code: string; message: string }) => {
    console.error("BankID authentication error:", error);
    // Error is already displayed by the BankID component
  };

  return (
    <div className="flex w-full justify-center">
      <Card className="  w-full max-w-md rounded-2xl border border-gray-100 bg-white shadow-xl transition-all duration-300 hover:shadow-2xl">
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="country-select" className="mb-2 block text-label">
              Select Country
            </label>
            <Select
              value={selectedCountry}
              onValueChange={(value) =>
                handleCountryChange(value as CountryCode)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={CountryCode.SE}>Sweden</SelectItem>
                  <SelectItem value={CountryCode.FI}>Finland</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-section">Log in with BankID</h2>
          </div>

          <BankIDAuth
            countryCode={selectedCountry}
            authenticationContext="LENDER_WEB"
            onSuccess={handleSuccess}
            onError={handleError}
            initialTile={null}
          />

          <div className="relative flex items-center py-2">
            <div className="grow border-t border-gray-200" />
            <span className="mx-4 shrink-0 text-label uppercase">Or</span>
            <div className="grow border-t border-gray-200" />
          </div>

          <div className="flex w-full justify-center">
            <Button variant="link" onClick={onSwitchToPassword}>
              Login with password instead
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
